import React from 'react';
import DiarizationResult from './DiarizationResult';
import SpeakerIcon from './SpeakerIcon';
import { DIARIZATION_PHASE } from './diarizationAdapter';
import {
    DIARIZATION_ERROR_MESSAGES,
    HAND_EDIT_REPLACED_NOTICE,
    RETRY_POINTS_NOTICE,
} from '../../../services/diarizationApi.js';
import { INSUFFICIENT_POINTS_MESSAGE } from '../../Matching/aiQuota.js';

/** `pending` の表示の文言 (SCR-D-001 §6.4) */
export const PENDING_MESSAGE =
    '話者分離を処理しています。結果が出るまで数分から最大 24 時間かかることがあります。この画面を閉じても処理は続きます。';

/** `loading`（受付の通信中。数秒）の表示の文言 (コード定義 CD-007) */
export const LOADING_MESSAGE = '話者分離処理中...';

/** 再実行ボタンの表示名 (SCR-D-001 BTN-D-003) */
export const RETRY_BUTTON_LABEL = '話者分離を再実行';

/**
 * 受け付けた時刻を「9/26 10:15」の形（JST）にする (SCR-D-001 §6.4「開始: 9/26 10:15」)。
 *
 * @param {string|null} iso API の accepted_at（UTC、末尾 Z）
 * @returns {string|null} 読めない値のときは null（時刻の行を出さない）
 */
export function formatAcceptedAt(iso) {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    const parts = new Intl.DateTimeFormat('ja-JP', {
        timeZone: 'Asia/Tokyo',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return `${get('month')}/${get('day')} ${get('hour')}:${get('minute')}`;
}

/**
 * 受付の後の失敗の文言。
 *
 * BE の failure.message を優先する（PBI-004 §6「画面に出せる文」）。文が空のときは汎用文（区分の表の
 * `stt_failed` と同じ文）にする。想定外の status は失敗の区分ではないため、再読み込みを促す専用の文にする。
 *
 * @param {Object|null} failure diarizationAdapter の failure
 * @returns {string}
 */
export function toFailureMessage(failure) {
    if (failure?.unknownStatus) return DIARIZATION_ERROR_MESSAGES.unknownStatus;
    return failure?.message || DIARIZATION_ERROR_MESSAGES.failed;
}

/**
 * 話者分離のエラーの文。エラーが無ければ null (SCR-D-001 §6.3)。
 *
 * タブの中のエラーの文（このパネル）と、タブのラベルの横の失敗の印・タブの外の通知（活動詳細の画面）の
 * 両方がこの関数で判定する（2 か所に別々に書くと片方だけが変わる。#1643 comment 6394254669）。
 * - 受付の失敗（executeError）: その文。受付の応答を受け取った画面にだけある
 * - 受付の後の失敗（状態が error）: failure の文。サーバが記録しているため、画面を読み直しても出る
 *   （受付で音声認識を始められなかった失敗も start_failed として記録される。#1643）
 * - 結果が出ている（done）ときは null。パネルは結果だけを描き、エラーの文を描かないため
 *   （印だけが出て、タブの中に指す先が無い状態を作らない）
 *
 * @param {{phase: string, failure: (Object|null), result: (Object|null), executeError: ({message: string}|null)}} diarization
 * @returns {string|null}
 */
export function diarizationErrorMessage({ phase, failure, result, executeError }) {
    if (phase === DIARIZATION_PHASE.done && result) return null;
    if (executeError) return executeError.message;
    return phase === DIARIZATION_PHASE.error ? toFailureMessage(failure) : null;
}

/**
 * 文字起こしタブの話者分離の部分 (SCR-D-001 §6.1・§6.3・§6.4)。
 *
 * - 結果あり（`done`）: 色分け・比率バー・トピック（`children` の既存の平文は出さない）
 * - 結果なし: 既存の平文（`children`）に、状態の表示（処理中・失敗）を添える
 *
 * @param {{
 *   diarization: {phase: string, acceptedAt: (string|null), failure: (Object|null), result: (Object|null),
 *                 executeError: (Object|null), isExecuting: boolean},
 *   canExecute: boolean,   // 再実行を押せるか。AI ポイントの残量が必要なポイント以上か (§5.2 No.1・§5.2.1)。
 *                          // 役割の判定からやり直せる失敗では常に true (0 ポイント。§4.3。#1246 段階 4)
 *   retryUnavailable?: boolean, // 面談の音声の応答に DIARIZATION のキーが無い (§4.3 BTN-D-003。#1664)。
 *                               // 役割の判定からやり直せる失敗では常に false (音声を使わないため)
 *   onRetry: () => void,   // BTN-D-003
 *   children: React.ReactNode,  // 既存の文字起こし（<pre>）
 * }} props
 */
function DiarizationPanel({ diarization, canExecute, retryUnavailable = false, onRetry, children }) {
    const { phase, acceptedAt, failure, result, executeError, isExecuting } = diarization;

    if (phase === DIARIZATION_PHASE.done && result) {
        return <DiarizationResult result={result} />;
    }

    // 再実行を出すか: 受付後の失敗は failure.retryable、受付の失敗は executeError.retryable（4xx は出さない。§6.3）。
    // キーが無い（サーバがいまの音声の受付を受け付けない）ときは出さない: 押せば必ず断られるボタンを出さない。
    // 残量不足と同じ扱い（押せない状態＋残量不足の文）にはしない——ポイントは足りており、文が事実と違うため。
    // 失敗の文は出したままにする (§4.3 BTN-D-003。#1664)
    const showRetry = !retryUnavailable && !isExecuting && (
        (executeError && executeError.retryable)
        || (!executeError && phase === DIARIZATION_PHASE.error && failure && failure.retryable)
    );
    const errorMessage = diarizationErrorMessage(diarization);
    const acceptedLabel = formatAcceptedAt(acceptedAt);

    return (
        <div className="diar-panel">
            <div aria-live="polite" className="diar-status-live">
                {phase === DIARIZATION_PHASE.loading && (
                    <p className="diar-status" data-testid="diar-loading">
                        <span className="spinner diar-status-spinner" aria-hidden="true" />
                        <span>{LOADING_MESSAGE}</span>
                    </p>
                )}
                {phase === DIARIZATION_PHASE.pending && (
                    <div className="diar-status" data-testid="diar-pending">
                        <span className="spinner diar-status-spinner" aria-hidden="true" />
                        <div>
                            <p className="diar-status-text">{PENDING_MESSAGE}</p>
                            {acceptedLabel && <p className="diar-status-sub">開始: {acceptedLabel}</p>}
                            {/* 利用者が本文を編集するのは、この表示が出ている間。処理中の表示の最後の行に出す
                                (§6.2「手で編集した内容が置き換わることの明示」③。#1755) */}
                            <p className="diar-status-sub" data-testid="diar-pending-hand-edit">
                                {HAND_EDIT_REPLACED_NOTICE}
                            </p>
                        </div>
                    </div>
                )}
            </div>
            {errorMessage && (
                <div className="diar-error" role="alert" data-testid="diar-error">
                    <p className="diar-error-text">
                        <SpeakerIcon name="triangle-alert" />
                        <span>{errorMessage}</span>
                    </p>
                    {showRetry && (
                        <div className="diar-retry">
                            <button
                                type="button"
                                // BTN-D-003 は Secondary。既存の副次ボタンのクラスを使う（新しいボタンの型を作らない）
                                className="modal-button-cancel"
                                onClick={onRetry}
                                disabled={!canExecute}
                            >
                                {RETRY_BUTTON_LABEL}
                            </button>
                            <p className="diar-retry-notice">
                                {canExecute ? RETRY_POINTS_NOTICE : INSUFFICIENT_POINTS_MESSAGE}
                            </p>
                            {/* 再実行のボタンを出すすべての場合に、消費の注意の次に出す
                                (§6.2「手で編集した内容が置き換わることの明示」②。#1755) */}
                            <p className="diar-retry-notice" data-testid="diar-retry-hand-edit">
                                {HAND_EDIT_REPLACED_NOTICE}
                            </p>
                        </div>
                    )}
                </div>
            )}
            {children}
        </div>
    );
}

export default DiarizationPanel;
